// PlanosGratis.jsx
import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiDownload, FiSearch, FiFilter, FiGrid, FiList,
    FiX, FiArrowRight, FiEye
} from 'react-icons/fi';
import SEO from '../components/SEO';
import { planosGratisData } from '../data/planosGratisData';
import { mapPlanoListToOldStructure } from '../data/planosMapper'; // 👈 Importa el mapper

// ✅ Constantes y utilidades
//const CATEGORIES = ['Todos', 'Vivienda', 'Comercial', 'Oficinas', 'Industrial', 'Educacional'];

// ✅ Función para obtener categorías dinámicas de los datos
const getCategoriesFromData = (data) => {
    const categories = new Set(data.map(item => item.category || 'Sin categoría'));
    return ['Todos', ...categories];
};

const CARD_VARIANTS = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.05,
            duration: 0.4,
            ease: [0.22, 1, 0.36, 1]
        }
    }),
    hover: {
        y: -8,
        transition: { duration: 0.2 }
    }
};

const PlanosGratis = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('Todos');
    const [viewMode, setViewMode] = useState('grid');
    const [showFilters, setShowFilters] = useState(false);

    // ✅ Mapear los datos a la estructura esperada
    const mappedPlanes = useMemo(() => {
        return mapPlanoListToOldStructure(planosGratisData);
    }, []);

    // ✅ Obtener categorías dinámicas
    const availableCategories = useMemo(() => {
        return getCategoriesFromData(planosGratisData);
    }, []);

    // ✅ Filtrar planos
    const filteredPlanes = useMemo(() => {
        return mappedPlanes.filter(plano => {
            const matchesSearch = plano.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                plano.descripcion.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = selectedCategory === 'Todos' || plano.categoria === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [searchTerm, selectedCategory, mappedPlanes]);

    // ✅ Resetear filtros
    const resetFilters = () => {
        setSearchTerm('');
        setSelectedCategory('Todos');
    };

    // ✅ Contador de resultados
    const resultCount = filteredPlanes.length;

    return (
        <div className="pt-32 pb-20 max-w-6xl mx-auto px-4 sm:px-6">
            <SEO
                title="Descarga Planos Arquitectónicos Gratis | Planix"
                description="Accede a nuestro catálogo de recursos y modelos de planos de referencia gratuitos listos para descargar. Encuentra inspiración para tu próximo proyecto."
                image="https://www.planixapp.com/cover.jpg"
                canonical="https://www.planixapp.com/planos-gratis"
                keywords="planos gratis, modelos de planos, arquitectura, descargar planos CAD PDF, Planix"
                type="website"
            />

            {/* ✅ Header mejorado */}
            <div className="text-center max-w-3xl mx-auto mb-12">
                <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="inline-block text-[#F26B1D] font-bold text-xs uppercase tracking-widest bg-[#F26B1D]/10 px-4 py-1.5 rounded-full"
                >
                    Recursos Abiertos
                </motion.span>
                <motion.h1
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-4xl md:text-5xl font-black text-[#1C1C1C] mt-4 mb-4 tracking-tight"
                >
                    Planos <span className="text-[#F26B1D]">Gratuitos</span>
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-gray-500 text-sm md:text-base leading-relaxed max-w-2xl mx-auto"
                >
                    Descarga modelos de referencia arquitectónica para inspirar tu próximo proyecto.
                    Recuerda que todo diseño conceptual debe ser validado por un profesional colegiado antes de construir.
                </motion.p>
            </div>

            {/* ✅ Barra de búsqueda y filtros */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mb-10"
            >
                <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
                    {/* Buscador */}
                    <div className="relative flex-1">
                        <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar planos por nombre o descripción..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#F26B1D]/50 focus:border-[#F26B1D] transition-all"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FiX />
                            </button>
                        )}
                    </div>

                    {/* Botones de filtro y vista */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`px-4 py-3 rounded-xl border transition-all flex items-center gap-2 text-sm whitespace-nowrap
                                ${showFilters || selectedCategory !== 'Todos'
                                    ? 'bg-[#F26B1D] text-white border-[#F26B1D]'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}
                        >
                            <FiFilter className="text-sm" />
                            Filtrar
                            {selectedCategory !== 'Todos' && (
                                <span className="ml-1 bg-white/20 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                                    1
                                </span>
                            )}
                        </button>

                        <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-3 transition-colors ${viewMode === 'grid' ? 'bg-[#F26B1D] text-white' : 'text-gray-400 hover:text-gray-600'}`}
                                aria-label="Vista en grid"
                            >
                                <FiGrid />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-3 transition-colors ${viewMode === 'list' ? 'bg-[#F26B1D] text-white' : 'text-gray-400 hover:text-gray-600'}`}
                                aria-label="Vista en lista"
                            >
                                <FiList />
                            </button>
                        </div>
                    </div>
                </div>

                {/* ✅ Filtros desplegables */}
                <AnimatePresence>
                    {showFilters && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                        >
                            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider mr-2">
                                        Categoría:
                                    </span>
                                    {availableCategories.map((cat) => (
                                        <button
                                            key={cat}
                                            onClick={() => setSelectedCategory(cat)}
                                            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all
                                                ${selectedCategory === cat
                                                    ? 'bg-[#F26B1D] text-white'
                                                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                    <button
                                        onClick={resetFilters}
                                        className="ml-auto text-xs text-[#F26B1D] hover:underline font-medium"
                                    >
                                        Limpiar filtros
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ✅ Contador de resultados */}
                <div className="mt-3 text-sm text-gray-500">
                    {resultCount > 0 ? (
                        <span>Mostrando <strong className="text-[#1C1C1C]">{resultCount}</strong> {resultCount === 1 ? 'plano' : 'planos'}</span>
                    ) : (
                        <span className="text-amber-600">No se encontraron planos con estos filtros</span>
                    )}
                </div>
            </motion.div>

            {/* ✅ Grid de planos */}
            <motion.div
                layout
                className={`grid gap-6 ${viewMode === 'grid'
                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                    : 'grid-cols-1'
                    }`}
            >
                <AnimatePresence>
                    {filteredPlanes.map((plano, index) => {
                        return (
                            <motion.div
                                key={plano.id}
                                custom={index}
                                initial="hidden"
                                animate="visible"
                                exit={{ opacity: 0, scale: 0.9 }}
                                variants={CARD_VARIANTS}
                                whileHover="hover"
                                className={`bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 flex flex-col ${viewMode === 'list' ? 'flex-row' : ''
                                    }`}
                            >
                                {/* ✅ Imagen mejorada */}
                                <Link
                                    to={`/planos-gratis/${plano.slug}`}
                                    className={`block group relative overflow-hidden ${viewMode === 'list' ? 'w-48 flex-shrink-0' : ''}`}
                                >
                                    <div className={`${viewMode === 'list' ? 'h-full' : 'h-52'} w-full bg-gray-100 relative overflow-hidden`}>
                                        <img
                                            src={plano.imagenes?.[0] || '/images/placeholder-plan.jpg'}
                                            alt={plano.titulo}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.src = '/images/placeholder-plan.jpg';
                                            }}
                                        />
                                        {/* ✅ Overlay con ícono de ver detalle */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                            <span className="bg-white/90 text-[#1C1C1C] px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform">
                                                <FiEye /> Ver detalles
                                            </span>
                                        </div>
                                        {/* ✅ Badge de categoría mejorado */}
                                        <span className="absolute top-3 left-3 bg-[#1C1C1C]/80 backdrop-blur-md text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1">
                                            {plano.categoria}
                                        </span>
                                        {/* ✅ Badge de descarga */}
                                        <span className="absolute bottom-3 right-3 bg-[#F26B1D]/90 backdrop-blur-md text-white text-[10px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                                            <FiDownload className="text-xs" /> PDF
                                        </span>
                                    </div>
                                </Link>

                                {/* ✅ Contenido de la tarjeta */}
                                <div className={`p-5 flex flex-col flex-1 ${viewMode === 'list' ? 'justify-between' : ''}`}>
                                    <div>
                                        <Link to={`/planos-gratis/${plano.slug}`}>
                                            <h3 className="text-lg font-bold text-[#1C1C1C] mb-2 group-hover:text-[#F26B1D] transition-colors line-clamp-2">
                                                {plano.titulo}
                                            </h3>
                                        </Link>
                                        <p className={`text-gray-500 text-sm leading-relaxed ${viewMode === 'list' ? 'line-clamp-2' : 'line-clamp-3'}`}>
                                            {plano.descripcion}
                                        </p>
                                    </div>

                                    {/* ✅ Botón de descarga mejorado */}
                                    <div className={`mt-4 ${viewMode === 'list' ? 'flex-shrink-0' : ''}`}>
                                        <Link
                                            to={`/planos-gratis/${plano.slug}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full bg-[#1C1C1C] hover:bg-[#F26B1D] text-white font-bold py-2.5 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-xs uppercase tracking-widest hover:shadow-lg transform hover:-translate-y-0.5"
                                        >
                                            <FiDownload className="text-base" />
                                            Descargar Archivo
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </motion.div>

            {/* ✅ Estado vacío */}
            {filteredPlanes.length === 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-16"
                >
                    <div className="text-6xl mb-4">🔍</div>
                    <h3 className="text-2xl font-bold text-[#1C1C1C] mb-2">No encontramos planos</h3>
                    <p className="text-gray-500 mb-6">
                        No hay planos que coincidan con "{searchTerm}" en la categoría "{selectedCategory}"
                    </p>
                    <button
                        onClick={resetFilters}
                        className="bg-[#F26B1D] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#E05D1A] transition-colors"
                    >
                        Limpiar filtros
                    </button>
                </motion.div>
            )}

            <motion.div
                className="mt-20 text-center"
            >
                <div className="text-center mt-12">
                    <Link
                        to="/planos"
                        className="inline-flex items-center gap-2 mt-6 bg-[#1C1C1C] hover:bg-[#F26B1D] text-white font-bold py-3 px-8 rounded-xl transition-all transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
                    >
                        Explora más planos
                    </Link>
                </div>
            </motion.div>

            {/* ✅ Sección de servicios al final */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="mt-20 pt-10 border-t border-gray-100 text-center"
            >
                <div className="max-w-2xl mx-auto">
                    <span className="inline-block text-[#F26B1D] font-bold text-xs uppercase tracking-widest bg-[#F26B1D]/10 px-4 py-1.5 rounded-full mb-4">
                        ¿Necesitas Más?
                    </span>
                    <h3 className="text-2xl font-bold text-[#1C1C1C] mb-2">
                        ¿Buscas un servicio profesional?
                    </h3>
                    <p className="text-gray-500 text-sm mb-6">
                        Además de planos de referencia, ofrecemos servicios de arquitectura, diseño y asesoría técnica.
                    </p>
                    <Link to="/">
                        <button
                            className="inline-flex items-center gap-2 bg-[#F26B1D] hover:bg-[#E05D1A] text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 text-sm group"
                            aria-label="Ver nuestros servicios"
                        >
                            Conoce nuestros servicios
                            <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </Link>
                </div>
            </motion.div>

            {/* ✅ Sección de asesoría mejorada */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="mt-16 p-6 sm:p-8 bg-gradient-to-br from-[#F26B1D]/5 to-[#F26B1D]/10 rounded-[2rem] border border-[#F26B1D]/20 text-center flex flex-col md:flex-row items-center justify-between gap-6"
            >
                <div className="text-left">
                    <h4 className="font-bold text-[#1C1C1C] text-lg flex items-center gap-2">
                        <span className="text-2xl">📐</span>
                        ¿Necesitas adaptar un plano a tus medidas exactas?
                    </h4>
                    <p className="text-gray-500 text-sm mt-1 max-w-lg">
                        Podemos personalizar cualquier diseño o ayudarte con los trámites de regularización y saneamiento.
                    </p>
                </div>
                <a
                    href="https://wa.me/51983974833"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group whitespace-nowrap bg-[#1C1C1C] hover:bg-[#F26B1D] text-white px-8 py-3.5 rounded-xl font-bold transition-all duration-300 uppercase text-xs tracking-widest flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                    Cotizar Asesoría
                    <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                </a>
            </motion.div>
        </div>
    );
};

export default PlanosGratis;